import { Controller } from "@hotwired/stimulus"

// Clickable button that submits a Plutonium interaction via JSON and
// surfaces validation errors as an inline toast. On success, the
// Order/KitchenTicket turbo broadcast replaces the affected card —
// nothing to do client-side. For actions that need a reason (void,
// cancel), set `prompt-value` and `param-key-value`; the controller
// collects the reason via window.prompt() and submits it under
// `<paramKey>[reason]`.
export default class extends Controller {
  static values = {
    url: String,
    confirm: String,
    prompt: String,
    paramKey: String
  }

  async click(event) {
    event.preventDefault()

    let reason = null
    if (this.hasPromptValue && this.promptValue) {
      reason = window.prompt(this.promptValue)
      if (reason === null) return // user cancelled
      reason = reason.trim()
      if (reason === "") {
        this.showToast("Reason is required", "error")
        return
      }
    } else if (this.hasConfirmValue && this.confirmValue) {
      if (!window.confirm(this.confirmValue)) return
    }

    this.element.disabled = true

    try {
      const formData = new FormData()
      if (reason !== null) {
        const key = this.hasParamKeyValue ? this.paramKeyValue : "interaction"
        formData.append(`${key}[reason]`, reason)
      }

      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: formData
      })

      if (response.ok) {
        // Success — the model's Turbo broadcast will replace the card.
        return
      }

      if (response.status === 422 || response.status === 403) {
        const payload = await response.json().catch(() => ({}))
        const message = this.formatErrors(payload)
        this.showToast(message, "error")
      } else {
        this.showToast(`Request failed (${response.status})`, "error")
      }
    } catch (err) {
      this.showToast(`Network error: ${err.message}`, "error")
    } finally {
      this.element.disabled = false
    }
  }

  formatErrors(payload) {
    const errors = payload?.errors
    if (Array.isArray(errors) && errors.length > 0) {
      return errors.map(e => e.full_message || e.message || e.detail).filter(Boolean).join(" · ")
    }
    return "Action failed"
  }

  csrfToken() {
    const el = document.querySelector("meta[name='csrf-token']")
    return el ? el.getAttribute("content") : ""
  }

  showToast(message, level = "info") {
    const container = this.ensureToastContainer()
    const toast = document.createElement("div")
    toast.textContent = message
    toast.style.cssText = `
      background: ${level === "error" ? "#991b1b" : "#1f2937"};
      color: #fff;
      padding: 10px 14px;
      border-radius: 8px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.35);
      font-size: 13px;
      max-width: 360px;
      opacity: 0;
      transform: translateY(-8px);
      transition: opacity 150ms ease-out, transform 150ms ease-out;
    `
    container.appendChild(toast)
    requestAnimationFrame(() => {
      toast.style.opacity = "1"
      toast.style.transform = "translateY(0)"
    })
    setTimeout(() => {
      toast.style.opacity = "0"
      toast.style.transform = "translateY(-8px)"
      setTimeout(() => toast.remove(), 200)
    }, 5000)
  }

  ensureToastContainer() {
    let container = document.getElementById("ops-toast-container")
    if (container) return container
    container = document.createElement("div")
    container.id = "ops-toast-container"
    container.style.cssText = `
      position: fixed;
      top: 16px;
      right: 16px;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      gap: 8px;
      pointer-events: none;
    `
    document.body.appendChild(container)
    return container
  }
}
